<template>
  <div class="json-obj-list-editor" :class="{ 'has-error': parseError }" :data-json-field="fieldKey">
    <b-table-simple small bordered responsive class="mb-1">
      <b-thead>
        <b-tr>
          <b-th v-for="col in columns" :key="col.key" :style="col.width ? { width: col.width } : {}">
            {{ col.label }}
          </b-th>
          <b-th style="width: 50px"></b-th>
        </b-tr>
      </b-thead>
      <b-tbody>
        <b-tr v-for="(row, idx) in rows" :key="idx">
          <b-td v-for="col in columns" :key="col.key">
            <b-form-select
              v-if="col.options"
              size="sm"
              v-model="row[col.key]"
              :options="col.options"
              @change="emitUpdate"
            />
            <b-form-input
              v-else
              size="sm"
              v-model="row[col.key]"
              :type="col.type === 'number' ? 'number' : 'text'"
              :placeholder="col.placeholder || ''"
              @input="emitUpdate"
            />
          </b-td>
          <b-td class="text-center align-middle">
            <b-button size="sm" variant="outline-danger" @click="removeRow(idx)" title="Remove">
              &times;
            </b-button>
          </b-td>
        </b-tr>
        <b-tr v-if="rows.length === 0">
          <b-td :colspan="columns.length + 1" class="text-center text-muted" style="font-style: italic; font-size: 13px;">
            No entries. Click "Add Row" below.
          </b-td>
        </b-tr>
      </b-tbody>
    </b-table-simple>
    <div class="d-flex align-items-center" style="gap: 8px;">
      <b-button size="sm" variant="outline-primary" @click="addRow">+ Add Row</b-button>
      <b-button v-if="allowRawJson" size="sm" variant="outline-secondary" @click="showRaw = !showRaw">
        {{ showRaw ? 'Hide' : 'Show' }} Raw JSON
      </b-button>
      <span v-if="parseError" class="text-danger" style="font-size: 12px;">{{ parseError }}</span>
    </div>
    <b-form-textarea
      v-if="allowRawJson && showRaw"
      :value="value"
      @input="onRawInput"
      rows="3"
      class="mt-2"
      style="font-family: monospace; font-size: 13px;"
    />
  </div>
</template>

<script>
export default {
  name: 'JsonObjectListEditor',
  props: {
    value: { type: String, default: '' },
    fieldKey: { type: String, default: '' },
    columns: {
      type: Array,
      required: true,
    },
    allowRawJson: { type: Boolean, default: false },
  },
  created() {
    this._parseTimer = null
  },
  beforeDestroy() {
    if (this._parseTimer) clearTimeout(this._parseTimer)
  },
  data() {
    return {
      rows: [],
      showRaw: false,
      parseError: '',
      internalUpdate: false,
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        if (this.internalUpdate) {
          this.internalUpdate = false
          return
        }
        this.parseValue(newVal)
      },
    },
    parseError(val) {
      if (val && this.allowRawJson) this.showRaw = true
    },
    allowRawJson(val) {
      if (!val) {
        this.showRaw = false
        if (this._parseTimer) {
          clearTimeout(this._parseTimer)
          this._parseTimer = null
        }
      }
    },
  },
  methods: {
    parseValue(jsonStr) {
      this.parseError = ''
      if (!jsonStr || jsonStr.trim() === '') {
        this.rows = []
        this.emitValidation('')
        return
      }
      try {
        const arr = typeof jsonStr === 'string' ? JSON.parse(jsonStr) : jsonStr
        if (Array.isArray(arr)) {
          this.rows = arr.map(item => {
            const row = {}
            for (const col of this.columns) {
              row[col.key] = item[col.key] !== undefined ? String(item[col.key]) : ''
            }
            return row
          })
          this.emitValidation('')
        } else {
          this.parseError = 'Expected a JSON array'
          this.rows = []
          this.emitValidation(this.parseError)
        }
      } catch (e) {
        this.parseError = 'Invalid JSON: ' + e.message
        this.rows = []
        this.emitValidation(this.parseError)
      }
    },
    buildArray() {
      return this.rows.map(row => {
        const obj = {}
        for (const col of this.columns) {
          const val = (row[col.key] || '').trim()
          if (val === '') continue
          if (col.type === 'number') {
            const num = Number(val)
            obj[col.key] = isNaN(num) ? val : num
          } else {
            obj[col.key] = val
          }
        }
        return obj
      }).filter(obj => Object.keys(obj).length > 0)
    },
    emitUpdate() {
      this.internalUpdate = true
      const arr = this.buildArray()
      const json = arr.length > 0 ? JSON.stringify(arr) : ''
      this.$emit('input', json)
    },
    onRawInput(val) {
      this.internalUpdate = true
      this.$emit('input', val)
      if (this._parseTimer) clearTimeout(this._parseTimer)
      this._parseTimer = setTimeout(() => {
        this.parseValue(val)
      }, 1000)
    },
    addRow() {
      const row = {}
      for (const col of this.columns) {
        row[col.key] = ''
      }
      this.rows.push(row)
    },
    removeRow(idx) {
      this.rows.splice(idx, 1)
      this.emitUpdate()
    },
    emitValidation(error) {
      if (this.fieldKey) {
        this.$emit('validation', this.fieldKey, error)
      }
    },
  },
}
</script>

<style scoped>
.json-obj-list-editor .table {
  margin-bottom: 4px;
}
.json-obj-list-editor .table td {
  padding: 4px 6px;
  vertical-align: middle;
}
.json-obj-list-editor .table th {
  padding: 4px 6px;
  font-size: 13px;
  font-weight: 600;
  background: #f8f9fa;
}
.json-obj-list-editor.has-error .table {
  border-color: #dc3545;
}
</style>
